// Empire Apps Chassis · connectors/financial/cams-cas
// Consolidated Account Statement (CAMS + Karvy / KFin) PDF upload + parse.
// Re-exports CAMS parser from mfapi-extended for ergonomic import path.

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type MutualFundFolio,
  fail,
  folioId,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';
import { getPdfBridge } from './adobe-pdf-bridge.ts';
import { parseCamsCasText } from './mfapi-extended.ts';

const STATUS_KEY = 'empire:financial:cams-cas:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly lastSync: string | null;
  readonly folioCount: number;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, lastSync: null, folioCount: 0 });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

export const camsCas: Connector = {
  id: 'cams-cas',
  displayName: 'CAMS / Karvy CAS upload',
  category: 'mutual-fund',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.folioCount,
      disabledReason: null,
    };
  },

  async connect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-cas-upload', 'CAMS+Karvy CAS PDF');
    const connectedAt = isoTs();
    writeStatus({ connected: true, lastSync: connectedAt, folioCount: 0 });
    return ok({ connectedAt });
  },

  async import(_ctx: ConnectorContext, opts?: { readonly fileBlob?: Blob }): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    if (!opts?.fileBlob) {
      writeStatus({ ...s, lastSync: isoTs(), folioCount: 1 });
      return ok(this.mockData(), 'No CAS PDF supplied. Showing mock preview.');
    }
    const bridge = getPdfBridge();
    const extract = await bridge.extract({ blob: opts.fileBlob });
    const rows = parseCamsCasText(extract.textFull);
    if (rows.length === 0) {
      return fail('No folios detected in CAS. Verify PDF password + format.', true);
    }
    const folios: readonly MutualFundFolio[] = rows.map((r) =>
      Object.freeze<MutualFundFolio>({
        folioId: folioId(`cas_${r.folioNumber}`),
        amc: r.amc,
        schemeCode: 'cas-import',
        schemeName: r.schemeName,
        units: r.units,
        latestNavPaise: paise(Math.round(r.currentValuePaise / Math.max(1, r.units))),
        currentValuePaise: paise(r.currentValuePaise),
        investedPaise: paise(r.costPaise),
        asOf: isoTs(),
      }),
    );
    writeStatus({ ...s, lastSync: isoTs(), folioCount: folios.length });
    return ok({ mfFolios: folios });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, lastSync: null, folioCount: 0 });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-cas-upload' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return {
      mfFolios: [
        Object.freeze<MutualFundFolio>({
          folioId: folioId('cas_2103850/82'),
          amc: 'Mirae Asset Mutual Fund',
          schemeCode: 'cas-import',
          schemeName: 'Mirae Asset Large & Midcap Fund Direct Plan Growth',
          units: 412.502,
          latestNavPaise: paise(982000),
          currentValuePaise: paise(40510000),
          investedPaise: paise(25200000),
          asOf: isoTs(),
        }),
      ],
    };
  },
};
